import { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Flipper, Flipped } from 'react-flip-toolkit';
import { Skeleton } from '@mui/material';

import { type DocumentType } from '@/gql';

import { nonFalsey } from 'Utils/nonFalsey';
import { createSubgroupColorGenerator } from '@/modules/group/utils/createSubgroupColourGenerator';
import { PinnedPostSummary } from 'Modules/group/noticeboard/post-collection/post/pinned-posts-collection/PinnedPostSummary';
import { Post } from '@/modules/group/noticeboard/post-collection/Post';
import type { MULTI_GROUPS_POST_FRAGMENT } from '@/app/(main)/myHub/home/_components/MultiGroupPostCollection';
import { Or2Dialog } from '@/components/feedback/Or2Dialog';

interface Props {
  posts: Array<DocumentType<typeof MULTI_GROUPS_POST_FRAGMENT>>;
  loading: boolean;
  machineName?: string;
}

export const MultiGroupPinnedPostCollection = ({
  posts,
  loading,
  machineName,
}: Props) => {
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

  const flipKey = posts
    .map((post) => String((post && post._id) + 'pin'))
    .join(':');

  const selectedPost = posts.find((post) => post._id === selectedPostId);
  const group = selectedPost?.owner;
  const getSubgroupColor = createSubgroupColorGenerator(
    group?.committees.filter(nonFalsey) ?? [],
  );

  // 2 states are needed so that the dialog closes and then the post is unselected, to avoid jitter
  useEffect(() => {
    if (selectedPost) {
      setPostDialogOpen(true);
    } else {
      // if a user unpins / deletes a post, make sure we cleanup
      setSelectedPostId(null);
      setPostDialogOpen(false);
    }
  }, [selectedPost]);

  if (posts.length === 0) {
    return null;
  }

  return loading ? (
    <>
      <Skeleton height={60} />
      <Skeleton height={60} />
    </>
  ) : (
    <>
      <Flipper flipKey={flipKey}>
        <Grid container spacing={3} paddingBottom={2}>
          {posts.map((post) => (
            <Flipped key={post?._id} flipId={post?._id ?? ''}>
              <Grid size={{ xs: 12, md: 6 }}>
                <PinnedPostSummary
                  post={post}
                  selected={selectedPostId === post._id}
                  setSelectedPostId={setSelectedPostId}
                  showOwner
                  or2Variant="inner"
                />
              </Grid>
            </Flipped>
          ))}
        </Grid>
      </Flipper>
      <Or2Dialog
        fullScreenThreshold="sm"
        aria-label="selected-post"
        title={'Pinned post - ' + selectedPost?.title}
        removeHeaderPadding
        open={postDialogOpen}
        onClose={() => setPostDialogOpen(false)}
        hasLoadingBar={false}
        slotProps={{
          transition: { onExited: () => setSelectedPostId(null) },
          content: {
            sx: {
              padding: 0,
              margin: 0,
            },
          },
        }}
      >
        {selectedPost && group && (
          <Post
            post={selectedPost}
            group={group}
            getSubgroupColor={getSubgroupColor}
            serviceMachineName={machineName}
            location={machineName ? 'adminHub' : 'myHub'}
          />
        )}
      </Or2Dialog>
    </>
  );
};
